import { useState } from 'react';
import { useTranslation } from '@michelin/central-provider';
import { useSnackbar } from 'notistack';
import { auth } from '../../../Auth';
import GLOBAL_CONFIG from '../../../global-variables';

const baseURL = GLOBAL_CONFIG.externalAPIUrl;

interface UseVehicleBulkImportProps {
  fleetId: string;
}

export const useVehicleBulkImport = ({ fleetId }: UseVehicleBulkImportProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const VALID_FILE_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  const VALID_FILE_EXTENSIONS = ['.xlsx'];

  // Helper to get auth token
  const getAuthToken = () => {
    const token = auth.getAuthResultTokenAccess();
    if (!token) throw new Error(t('Authorization token is missing or invalid.'));
    return token;
  };

  const downloadTemplate = async (): Promise<boolean> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${baseURL}/cp-be/bulk-vehicle-import/template`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-cache',
      });
      if (!response.ok) throw new Error(t('Failed to download template'));
      const base64 = await response.text();
      if (!base64 || base64.length < 100) {
        enqueueSnackbar(t('Downloaded file is empty or invalid.'), { variant: 'error' });
        return false;
      }
      const byteArray = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      if (blob.size === 0) {
        enqueueSnackbar(t('Downloaded file is empty or corrupted.'), { variant: 'error' });
        return false;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vehicle-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar(t('Template downloaded successfully'), { variant: 'success' });
      return true;
    } catch (error) {
      enqueueSnackbar((error as Error).message || t('Failed to download template'), { variant: 'error' });
      return false;
    }
  };

  const validateFileType = (file: File): string | null => {
    if (
      !VALID_FILE_TYPES.includes(file.type) &&
      !VALID_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
    ) {
      return t('Invalid file type. Please upload a valid Excel file.');
    }
    return null;
  };

  const getPresignedUrl = async (): Promise<{ url: string; fileID: string }> => {
    const token = getAuthToken();
    const response = await fetch(`${baseURL}/cp-be/bulk-vehicle-import/presigned-url`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-cache',
    });
    return response.json();
  };

  const uploadAndValidateFile = async (file: File): Promise<any> => {
    setIsUploading(true);
    const validationError = validateFileType(file);
    if (validationError) {
      setIsUploading(false);
      enqueueSnackbar(validationError, { variant: 'error' });
      throw new Error(validationError);
    }
    // Step 1: Get presigned URL
    let uploadUrl: string;
    let uploadFileID: string;
    try {
      const presigned = await getPresignedUrl();
      uploadUrl = presigned.url;
      uploadFileID = presigned.fileID;
    } catch (e) {
      setIsUploading(false);
      enqueueSnackbar(t('There was an error preparing upload. Please try again later'), { variant: 'error' });
      throw e;
    }
    // Step 2: Upload file
    try {
      const uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: file });
      if (!uploadResponse.ok) throw new Error(t('Failed to upload file to presigned URL'));
    } catch (e) {
      setIsUploading(false);
      enqueueSnackbar(t('There was an error uploading the file. Please try again later'), { variant: 'error' });
      throw e;
    }
    // Step 3: Validate data
    try {
      const token = getAuthToken();
      const validateResponse = await fetch(`${baseURL}/cp-be/bulk-vehicle-import/validate/${fleetId}/${uploadFileID}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!validateResponse.ok) {
        console.log('Validate response not ok', validateResponse);
        const jsonError = await validateResponse.json();
        const errorMessage = jsonError.error || jsonError.message || t('Failed to validate uploaded file');
        throw new Error(errorMessage);
      }
      const result = await validateResponse.json();
      setIsUploading(false);
      return { ...result, fileID: uploadFileID };
    } catch (e) {
      setIsUploading(false);
      enqueueSnackbar((e as Error).message || t('Failed to upload or validate file'), { variant: 'error' });
      throw e;
    }
  };

  const confirmImport = async (fileID: string): Promise<any> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${baseURL}/cp-be/bulk-vehicle-import/confirm/${fleetId}/${fileID}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const jsonError = await response.json();
        const errorMessage = jsonError.errors || jsonError.message || t('Failed to confirm import');
        throw new Error(errorMessage);
      }
      return response.json();
    } catch (error) {
      enqueueSnackbar((error as Error).message || t('Failed to confirm import'), { variant: 'error' });
      throw error;
    }
  };

  return {
    downloadTemplate,
    validateFileType,
    uploadAndValidateFile,
    confirmImport,
    isUploading,
  };
};
